import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { MaintenanceRequest } from '../types/properties';
import { MaintenanceRequestCard } from '../components/maintenance/MaintenanceRequestCard';
import { Wrench, Filter, Search } from 'lucide-react';
import { Modal } from '../components/Modal';

export function Maintenance() {
  const [requests, setRequests] = useState<(MaintenanceRequest & { properties: { address: string } })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      // Using a join to get property address
      const { data, error } = await supabase
        .from('maintenance_requests')
        .select(`
          *,
          properties ( address )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests((data as any) || []);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRequests = requests.filter(req => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return ['new', 'assigned', 'in-progress'].includes(req.status);
    return req.status === statusFilter;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <div className="flex items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-warning-dim)] flex items-center justify-center">
            <Wrench className="text-[var(--t-warning)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)] m-0">Maintenance Dashboard</h1>
            <p className="text-sm text-[var(--t-text-muted)] mt-1">Track and manage property repairs</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] appearance-none"
            >
              <option value="all">All Requests</option>
              <option value="active">Active (New/Assigned/Progress)</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-8 bg-[var(--t-bg)]">
        {isLoading ? (
          <div className="text-[var(--t-text-muted)]">Loading requests...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center p-12 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl">
            <Wrench size={48} className="mx-auto text-[var(--t-border-hover)] mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Maintenance Requests</h3>
            <p className="text-[var(--t-text-muted)]">There are no requests matching your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRequests.map(req => (
              <MaintenanceRequestCard
                key={req.id}
                request={req}
                propertyName={req.properties?.address}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
